"use client";

import { useCallback, useSyncExternalStore } from "react";

/**
 * Whether a CSS media query currently matches.
 *
 * The same mechanism `useIsMobile` uses, generalised: the browser notifies on the
 * breakpoint crossing rather than on every resized pixel, and there's no state to
 * synchronise. The server snapshot is `false`, since the server can't know the
 * viewport — the real value arrives on the first client render, before paint.
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      const list = window.matchMedia(query);
      list.addEventListener("change", onChange);
      return () => list.removeEventListener("change", onChange);
    },
    [query]
  );

  const getSnapshot = useCallback(() => window.matchMedia(query).matches, [query]);

  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}
